// Screenshot markup: boxes, arrows, text, a pen, blur (pixelates, so it can't be undone in the saved
// file) and crop, drawn on a canvas at the image's full size. Saves a new PNG; the original is kept.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Crop, Droplets, PenLine, Square, Type, Undo2 } from 'lucide-react';
import { Button, cx } from '../../ui';

type Pt = [number, number];
type Tool = 'box' | 'arrow' | 'text' | 'pen' | 'blur' | 'crop';
type Shape = { t: Exclude<Tool, 'crop'>; color: string; pts: Pt[]; text?: string };

const COLORS = [['#D64545', 'Red'], ['#E3A13B', 'Amber'], ['#3F7FD1', 'Blue'], ['#2F8F4E', 'Green']] as const;
const TOOLS: Array<[Tool, string, typeof Square]> = [
  ['box', 'Box', Square], ['arrow', 'Arrow', ArrowUpRight], ['text', 'Text', Type],
  ['pen', 'Pen', PenLine], ['blur', 'Blur', Droplets], ['crop', 'Crop', Crop],
];

function rectOf(a: Pt, b: Pt) {
  return { x: Math.min(a[0], b[0]), y: Math.min(a[1], b[1]), w: Math.abs(a[0] - b[0]), h: Math.abs(a[1] - b[1]) };
}

function pixelate(ctx: CanvasRenderingContext2D, img: HTMLImageElement, a: Pt, b: Pt, block: number) {
  const r = rectOf(a, b);
  if (r.w < 2 || r.h < 2) return;
  const small = document.createElement('canvas');
  small.width = Math.max(1, Math.round(r.w / block));
  small.height = Math.max(1, Math.round(r.h / block));
  const s = small.getContext('2d')!;
  s.drawImage(img, r.x, r.y, r.w, r.h, 0, 0, small.width, small.height);
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(small, 0, 0, small.width, small.height, r.x, r.y, r.w, r.h);
  ctx.restore();
}

function drawShape(ctx: CanvasRenderingContext2D, s: Shape, lw: number, font: number) {
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = lw;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const [a, b] = s.pts as [Pt, Pt];
  if (s.t === 'box' && b) {
    const r = rectOf(a, b);
    ctx.strokeRect(r.x, r.y, r.w, r.h);
  } else if (s.t === 'arrow' && b) {
    const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
    const head = lw * 4;
    ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(b[0], b[1]);
    ctx.lineTo(b[0] - head * Math.cos(ang - Math.PI / 7), b[1] - head * Math.sin(ang - Math.PI / 7));
    ctx.lineTo(b[0] - head * Math.cos(ang + Math.PI / 7), b[1] - head * Math.sin(ang + Math.PI / 7));
    ctx.closePath(); ctx.fill();
  } else if (s.t === 'pen' && s.pts.length > 1) {
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    for (const p of s.pts.slice(1)) ctx.lineTo(p[0], p[1]);
    ctx.stroke();
  } else if (s.t === 'text' && s.text) {
    ctx.font = `600 ${font}px Inter, system-ui, sans-serif`;
    const pad = font * 0.35;
    const w = ctx.measureText(s.text).width;
    ctx.fillRect(a[0], a[1], w + pad * 2, font + pad * 2);
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'top';
    ctx.fillText(s.text, a[0] + pad, a[1] + pad);
  }
}

export function MarkupEditor({ src, onSave, onCancel, saving }: { src: string; onSave: (png: Blob) => void; onCancel: () => void; saving?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Tool>('box');
  const [color, setColor] = useState<string>(COLORS[0][0]);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [draft, setDraft] = useState<Shape | null>(null);
  const [crop, setCrop] = useState<{ a: Pt; b: Pt } | null>(null);
  const [typing, setTyping] = useState<{ at: Pt; css: { left: number; top: number }; value: string } | null>(null);
  const drag = useRef<Pt | null>(null);
  // Pointer events can outrun renders; the live shape and crop are kept in refs as well as state.
  const live = useRef<Shape | null>(null);
  const liveCrop = useRef<{ a: Pt; b: Pt } | null>(null);
  const setLive = (d: Shape | null) => { live.current = d; setDraft(d); };
  const setLiveCrop = (c: { a: Pt; b: Pt } | null) => { liveCrop.current = c; setCrop(c); };

  useEffect(() => {
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = src;
  }, [src]);

  const lw = img ? Math.max(3, Math.round(img.naturalWidth / 360)) : 3;
  const font = img ? Math.max(16, Math.round(img.naturalWidth / 55)) : 16;

  const paint = useCallback((ctx: CanvasRenderingContext2D, all: Shape[]) => {
    if (!img) return;
    ctx.clearRect(0, 0, img.naturalWidth, img.naturalHeight);
    ctx.drawImage(img, 0, 0);
    // Blur first, so it covers what is under it and not the marks on top.
    for (const s of all) {
      if (s.t !== 'blur' || !s.pts[1]) continue;
      // Blocks at least half the box height, so text can't be read back out of the saved copy.
      const r = rectOf(s.pts[0]!, s.pts[1]);
      pixelate(ctx, img, s.pts[0]!, s.pts[1], Math.max(12, Math.round(img.naturalWidth / 90), Math.round(Math.min(r.w, r.h) / 2)));
    }
    for (const s of all) if (s.t !== 'blur') drawShape(ctx, s, lw, font);
  }, [img, lw, font]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || !img) return;
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    const ctx = c.getContext('2d')!;
    paint(ctx, draft ? [...shapes, draft] : shapes);
    if (crop) {
      const r = rectOf(crop.a, crop.b);
      ctx.save();
      ctx.fillStyle = 'rgba(17,22,29,0.55)';
      ctx.beginPath();
      ctx.rect(0, 0, c.width, c.height);
      ctx.rect(r.x, r.y, r.w, r.h);
      ctx.fill('evenodd');
      ctx.strokeStyle = '#ffffff';
      ctx.setLineDash([lw * 3, lw * 2]);
      ctx.lineWidth = Math.max(2, lw / 2);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      ctx.restore();
    }
  }, [img, shapes, draft, crop, paint, lw]);

  const toImage = (e: React.PointerEvent): Pt => {
    const r = canvas.current!.getBoundingClientRect();
    const sx = img!.naturalWidth / r.width;
    return [Math.round((e.clientX - r.left) * sx), Math.round((e.clientY - r.top) * sx)];
  };

  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!img || e.button !== 0) return;
    const p = toImage(e);
    if (tool === 'text') return; // opened on click, after the mouse is up, so the box keeps focus
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = p;
    if (tool === 'crop') setLiveCrop({ a: p, b: p });
    else setLive({ t: tool, color, pts: [p, p] });
  };
  const extend = (p: Pt) => {
    if (tool === 'crop') { if (liveCrop.current) setLiveCrop({ ...liveCrop.current, b: p }); return; }
    const d = live.current;
    if (d) setLive({ ...d, pts: d.t === 'pen' ? [...d.pts, p] : [d.pts[0]!, p] });
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drag.current || !img) return;
    extend(toImage(e));
  };
  const up = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drag.current) return;
    drag.current = null;
    if (img) extend(toImage(e));
    const d = live.current;
    if (d) {
      const r = rectOf(d.pts[0]!, d.pts[d.pts.length - 1]!);
      if (d.t === 'pen' ? d.pts.length > 2 : r.w + r.h > 6) setShapes((s) => [...s, d]);
      setLive(null);
    }
    const c = liveCrop.current;
    if (c) {
      const r = rectOf(c.a, c.b);
      if (r.w < 10 || r.h < 10) setLiveCrop(null);
    }
  };
  const openText = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool !== 'text' || !img) return;
    const r = canvas.current!.getBoundingClientRect();
    const host = canvas.current!.parentElement!.getBoundingClientRect();
    const sx = img.naturalWidth / r.width;
    setTyping({ at: [Math.round((e.clientX - r.left) * sx), Math.round((e.clientY - r.top) * sx)], css: { left: e.clientX - host.left, top: e.clientY - host.top }, value: '' });
  };
  const commitText = () => {
    if (typing?.value.trim()) setShapes((s) => [...s, { t: 'text', color, pts: [typing.at], text: typing.value.trim() }]);
    setTyping(null);
  };
  const undo = () => { if (crop) setLiveCrop(null); else setShapes((s) => s.slice(0, -1)); };

  const save = () => {
    if (!img) return;
    const full = document.createElement('canvas');
    full.width = img.naturalWidth;
    full.height = img.naturalHeight;
    paint(full.getContext('2d')!, shapes);
    let out = full;
    if (crop) {
      const r = rectOf(crop.a, crop.b);
      out = document.createElement('canvas');
      out.width = r.w;
      out.height = r.h;
      out.getContext('2d')!.drawImage(full, r.x, r.y, r.w, r.h, 0, 0, r.w, r.h);
    }
    out.toBlob((b) => { if (b) onSave(b); }, 'image/png');
  };

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (typing || (e.target as HTMLElement).closest('input, textarea, select')) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); save(); return; }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });

  const dirty = shapes.length > 0 || crop !== null;
  return (
    <div className="flex min-h-0 flex-1 gap-3">
      <div role="toolbar" aria-label="Markup tools" aria-orientation="vertical" className="flex flex-none flex-col items-center gap-1">
        {TOOLS.map(([t, name, Icon]) => (
          <button key={t} type="button" aria-pressed={tool === t} onClick={() => setTool(t)}
            className={cx('flex h-[50px] w-[52px] flex-col items-center justify-center gap-0.5 rounded-[10px] border-0 text-[10px] font-semibold',
              tool === t ? 'bg-[#2B3544] text-white shadow-[inset_0_0_0_1px_#4A5870]' : 'bg-transparent text-[#C9D1E0] hover:bg-[#1B222C]')}>
            <Icon aria-hidden size={17} />{name}
          </button>
        ))}
        <div className="my-1.5 h-px w-9 bg-[#263040]" />
        {COLORS.map(([c, name]) => (
          <button key={c} type="button" aria-label={`${name} marks`} aria-pressed={color === c} onClick={() => setColor(c)}
            style={{ background: c }} className={cx('h-6 w-6 rounded-full border-2 border-[#11161D]', color === c ? 'ring-2 ring-white' : 'ring-1 ring-[#4A5870]')} />
        ))}
        <div className="my-1.5 h-px w-9 bg-[#263040]" />
        <button type="button" title="Undo" aria-label="Undo" disabled={!dirty} onClick={undo}
          className="flex h-10 w-10 items-center justify-center rounded-[10px] border-0 bg-transparent text-[#C9D1E0] hover:bg-[#1B222C] disabled:opacity-40"><Undo2 aria-hidden size={17} /></button>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="relative flex min-h-0 flex-1 items-center justify-center">
          {!img && <span className="text-sm text-[#AEB8CC]">Loading image…</span>}
          <canvas ref={canvas} onClick={openText} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
            aria-label="Screenshot being marked up"
            className={cx('max-h-[70vh] max-w-full rounded-md bg-white shadow-[0_20px_50px_rgba(0,0,0,0.5)]', tool === 'text' ? 'cursor-text' : 'cursor-crosshair', !img && 'hidden')}
            style={{ touchAction: 'none' }} />
          {typing && (
            <input autoFocus aria-label="Text to add" value={typing.value}
              onChange={(e) => setTyping({ ...typing, value: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') { e.stopPropagation(); setTyping(null); } }}
              onBlur={commitText}
              style={{ left: typing.css.left, top: typing.css.top, borderColor: color }}
              className="absolute z-10 h-9 w-64 rounded-md border-2 bg-white px-2 text-sm text-ink outline-none" placeholder="Type, then Enter" />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#AEB8CC]">
          <span>{tool === 'blur' ? 'Drag over names, SSNs or amounts. Blur is permanent in the saved copy.' : tool === 'crop' ? 'Drag the area to keep.' : tool === 'text' ? 'Click where the text goes.' : 'Drag on the image.'}</span>
          <span className="ml-auto flex gap-2">
            <Button size="sm" onClick={onCancel} className="!border-[#3A4556] !bg-[#1B222C] !text-[#E6EAF0]">Cancel</Button>
            <Button size="sm" variant="primary" disabled={!dirty || saving} onClick={save} className="!bg-white !text-ink disabled:!bg-[#2B3544] disabled:!text-[#7D889A]">{saving ? 'Saving…' : 'Save markup'}</Button>
          </span>
        </div>
      </div>
    </div>
  );
}
